import React, { useRef } from 'react';
import { getPageThemeClass } from 'lib/utils/get-page-theme-class';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import {
  Field,
  ImageField,
  Item,
  LinkField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { tv } from 'tailwind-variants';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { WistiaPlayer, WistiaPlayerElement } from '@wistia/wistia-player-react';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import useDictionary from 'lib/hooks/useDictionary';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import { Location } from 'helpers/MetaData';
import { getUserFacingProviderSpecialties } from 'lib/utils/get-user-facing-provider-specialties';
import { SpecialtyItem } from '../../../../../types/provider-items';
import { Settings } from '.generated/Feature.CW.model';

type DegreeItem = {
  fields: {
    value: Field<string>;
  };
};

type schedulingType = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    ctaLabelText: Field<string>;
    cta: LinkField;
    name: Field<string>;
    specialties: SpecialtyItem[];
  };
};

type acceptingNewPatients = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    name: Field<string>;
    value: Field<string>;
  };
};

type SpecialtiesProps = {
  specialties: SpecialtyItem[];
};

export type PageSettings = Settings.SiteSettings['fields'];

//specialities
const Specialties = ({ specialties }: SpecialtiesProps) => {
  if (!specialties || specialties.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center mt-8">
      {specialties.map((item, index) => {
        const { specialtyLabelText, specialtyLabelLink } = item.fields;
        const isLast = index === specialties.length - 1;

        return (
          <span key={item.id} className="flex items-center pr-4 relative">
            <LinkWrapper
              field={specialtyLabelLink}
              ctaVariant="tertiary"
              className="!bg-transparent hover:!bg-transparent"
            >
              <PlainTextWrapper field={specialtyLabelText} />
            </LinkWrapper>

            {!isLast && (
              <span className="ml-4 h-[25px] w-px bg-gray-100 inline-block align-middle" />
            )}
          </span>
        );
      })}
    </div>
  );
};

const ProviderHeader = (): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext;
  const templateID = contextWithSettings?.route?.templateId as string;
  const itemId = contextWithSettings?.route?.itemId as string;
  const fields = sitecoreContext?.route?.fields || {};
  const siteSettings = sitecoreContext?.siteSettings as PageSettings;
  const mbhPhoneNumber = siteSettings?.mbhPhoneNumber?.value; // Mental & Behavioral Health phone number
  const { getDictionaryValue } = useDictionary();

  const {
    photo,
    firstName,
    middleName,
    lastName,
    degreeTitles,
    additionalDegreeTitles,
    specialties,
    acceptingNewPatients,
    patientReferralLabel,
    patientReferralLink,
    videoID,
    phone,
    locations,
    schedulingType,
    requestAnAppointment,
    providerType,
    epicPhysicianID,
  } = fields as unknown as {
    photo: ImageField;
    firstName: Field<string>;
    middleName: Field<string>;
    lastName: Field<string>;
    degreeTitles: DegreeItem[];
    additionalDegreeTitles: DegreeItem[];
    specialties: SpecialtyItem[];
    acceptingNewPatients: acceptingNewPatients;
    patientReferralLabel: Field<string>;
    patientReferralLink: LinkField;
    videoID: Field<string>;
    phone: Field<string>;
    schedulingType: schedulingType;
    requestAnAppointment: LinkField;
    locations: Location[];
    providerType: Item;
    epicPhysicianID: Field<string>;
  };

  const theme = getPageThemeClass(templateID);
  const degreeList = degreeTitles?.map((deg) => deg?.fields?.value)?.filter((d) => d?.value) ?? [];
  const additionalDegreeList =
    additionalDegreeTitles?.map((deg) => deg?.fields?.value)?.filter((d) => d?.value) ?? [];
  const combinedDegreeList = [...degreeList, ...additionalDegreeList];

  const filteredSpecialties = getUserFacingProviderSpecialties(specialties, providerType) ?? [];
  // specialties is coming as array of objects like your sample
  const specialtiesArray =
    Array.isArray(specialties) && specialties.length > 0
      ? specialties.map((spec: SpecialtyItem) =>
          spec?.fields?.specialtyLabelText?.value?.trim().toLowerCase()
        )
      : [];
  // Case-insensitive check
  const hasMBH = specialtiesArray.includes('mental & behavioral health');

  //const SpecialityList = specialties?.map((speciality) => speciality?.fields?.value)?.filter((d) => d?.value) ?? [];
  const {
    base,
    HeaderContentWrapper,
    ProviderContent,
    ImageContentWrapper,
    ProviderImage,
    ProviderProxyImage,
    Title,
  } = TAILWIND_VARIANTS();
  const isEditing = useIsEditing();
  const player = useRef<WistiaPlayerElement | null>(null);
  // When our custom button is clicked, play the video.
  function handleClick() {
    if (player.current === null) {
      return;
    }
    player.current.play();
  }

  const { open } = useAppointmentModal();
  // const phoneNumber = locations?.[0]?.fields?.mobileVisiblePhoneNumber
  //   ? locations?.[0]?.fields?.mobileVisiblePhoneNumber
  //   : { value: '' };
  // pick default phone number from location
  const basePhoneNumber = locations?.[0]?.fields?.mobileVisiblePhoneNumber
    ? locations[0].fields.mobileVisiblePhoneNumber
    : { value: '' };

  // override with MBH number if applicable
  const finalPhoneNumber = hasMBH && mbhPhoneNumber ? { value: mbhPhoneNumber } : basePhoneNumber;

  const specialtiesText = specialties?.map(
    (specialty) => specialty?.fields?.specialtyLabelText?.value
  );

  const schedulingTypeLabel = schedulingType?.fields?.name?.value
    ?.replaceAll(' ', '')
    ?.toLowerCase();
  const queryString = `?providerName=${firstName?.value} ${lastName?.value}&specialties=${specialtiesText?.join(', ')}`;

  React.useEffect(() => {
    const wistiaElement: WistiaPlayerElement | null = document?.querySelector('.wistia-player');
    if (wistiaElement && wistiaElement !== null) {
      wistiaElement.tabIndex = -1;
    }
  }, []);

  return (
    <ComponentWrapper
      aria-label={`Provider Profile Header - ${firstName?.value} ${lastName?.value}`}
      params={{
        Theme: theme,
      }}
      ariaLabel={`Provider Profile Header - ${firstName?.value} ${lastName?.value}`}
      role="region"
      className={base()}
    >
      <div className={HeaderContentWrapper()}>
        <div className={ProviderContent()}>
          <div className={ImageContentWrapper()}>
            <div className="relative">
              {photo?.value?.src ? (
                <ImageWrapper className={ProviderImage()} field={photo} />
              ) : (
                <div className={ProviderProxyImage()}>
                  <SvgIcon
                    icon="user"
                    className="!h-[150px] !w-[150px] [&>path]:fill-darkblue-base"
                    size="lg"
                    aria-label="Provider Profile Video"
                  />
                </div>
              )}
              {videoID?.value && (
                <ButtonWrapper
                  ctaVariant="tertiary"
                  onClick={handleClick}
                  className="lg:hidden flex absolute right-10 bottom-10 z-1 h-[48px] w-[48px] bg-theme-bg !rounded-full shadow-default "
                >
                  <SvgIcon
                    icon="image-play"
                    className="!h-[22px] !w-[22px] !ml-[0]"
                    size="lg"
                    aria-label="Provider Profile Video"
                  />
                </ButtonWrapper>
              )}
            </div>
            {videoID?.value && (
              <ButtonWrapper
                ctaVariant="tertiary"
                ctaIcon="play"
                text={getDictionaryValue('WatchVideo')}
                onClick={handleClick}
                className="w-full lg:flex hidden"
              />
            )}
            <WistiaPlayer
              ref={player}
              mediaId={videoID?.value}
              className="h-0 w-0 opacity-0 wistia-player"
              controlsVisibleOnLoad={false}
              endVideoBehavior="reset"
              wistiaPopover={true}
              popoverPreventScroll={true}
            />
          </div>
          <div className="lg:max-w-[767px] max-w-full">
            <h1 className={Title()}>
              <PlainTextWrapper field={firstName} /> <PlainTextWrapper field={middleName} />{' '}
              <PlainTextWrapper field={lastName} />
              {(firstName || middleName || lastName) && <span>, </span>}
              {combinedDegreeList.length > 0 && (
                <span>
                  <span> </span>
                  {combinedDegreeList.map((deg, index) => (
                    <span key={index}>
                      <PlainTextWrapper field={deg} />
                      {index < combinedDegreeList.length - 1 && <span className="mx-1">,</span>}
                    </span>
                  ))}
                </span>
              )}
            </h1>
            <div>
              <Specialties specialties={filteredSpecialties} />
            </div>

            {acceptingNewPatients ? (
              acceptingNewPatients?.fields?.value?.value ? (
                <div className="text-gray-900 font-nunitoSans text-18 font-normal leading-[31px] flex lg:items-center items-start lg:mt-16 mt-4">
                  {acceptingNewPatients?.fields?.name?.value.toLowerCase().trim() ===
                  'not accepting new patients' ? (
                    <SvgIcon
                      icon={'xcircle'}
                      className={'[&>path]:fill-utility-red-700 !h-[22px] !w-[22px] mr-4'}
                      size="sm"
                      aria-label={acceptingNewPatients?.fields?.name?.value}
                    />
                  ) : (
                    <SvgIcon
                      icon={'check-circle'}
                      className={'[&>path]:fill-utility-green-700 mr-4 !h-[22px] !w-[22px]'}
                      size="sm"
                      aria-label={acceptingNewPatients?.fields?.name?.value}
                    />
                  )}
                  <PlainTextWrapper field={acceptingNewPatients?.fields?.value} />
                </div>
              ) : (
                <div className="text-gray-900 font-nunitoSans text-18 font-normal leading-[31px] flex lg:items-center items-start lg:mt-16 mt-4">
                  <SvgIcon
                    icon={'xcircle'}
                    className={'[&>path]:fill-utility-red-700 !h-[22px] !w-[22px] mr-4'}
                    size="sm"
                    aria-label={acceptingNewPatients?.fields?.name?.value}
                  />

                  <div>{getDictionaryValue('NotAcceptingNewPatients')}</div>
                </div>
              )
            ) : (
              <></>
            )}
            {/* CTA start */}
            <div className="lg:block md:hidden block">
              <div className="flex flex-wrap gap-12 lg:mt-16 mt-[26px]">
                {schedulingTypeLabel?.toLocaleLowerCase() === 'primarycare' &&
                epicPhysicianID?.value ? (
                  <ButtonWrapper
                    text={schedulingType?.fields?.ctaLabelText?.value} // todo: when we work on appoinment model
                    ctaVariant="primary"
                    className="min-w-[281px] w-full md:w-auto"
                    onClick={() => open(itemId, queryString, finalPhoneNumber?.value || '')}
                  />
                ) : (
                  <>
                    {schedulingType?.fields?.cta?.value?.href && (
                      <LinkWrapper
                        field={{
                          value: {
                            href: `${schedulingType?.fields?.cta?.value?.href}${queryString}`,
                            text: schedulingType?.fields?.ctaLabelText?.value,
                            title: schedulingType?.fields?.ctaLabelText?.value,
                            target: schedulingType?.fields?.cta?.value?.target,
                          },
                        }}
                        suppressNewTabIcon={true}
                        ctaVariant="primary"
                        className="min-w-[281px]"
                      />
                    )}
                  </>
                )}
                {(finalPhoneNumber?.value || isEditing) && (
                  <LinkWrapper
                    ctaVariant="secondary"
                    field={{
                      value: {
                        href: `tel:${finalPhoneNumber?.value}`,
                      },
                    }}
                    className="lg:inline-block block md:w-fit w-full"
                  >
                    <PlainTextWrapper field={finalPhoneNumber} />
                  </LinkWrapper>
                )}
              </div>
              <div className="flex items-center lg:mt-16 mt-12">
                <div className="font-nunitoSans text-18 leading-[31px] text-gray-900 font-bold">
                  <PlainTextWrapper field={patientReferralLabel} />
                </div>
                <LinkWrapper ctaVariant="hyperlink" field={patientReferralLink} className="ml-4" />
              </div>
            </div>
            {/* CTA start */}
          </div>
        </div>
        {/* Tablet CTA start */}
        <div className="lg:hidden md:block hidden">
          <div className="flex flex-wrap gap-12 lg:mt-16 mt-[26px]">
            {schedulingType?.fields?.name?.value?.toLocaleLowerCase() === 'primary care' ? (
              <LinkWrapper
                ctaVariant="primary"
                field={{
                  value: {
                    href: '/',
                  },
                }}
                className="lg:inline-block block md:w-fit w-full"
              >
                <PlainTextWrapper field={schedulingType?.fields?.ctaLabelText} />
              </LinkWrapper>
            ) : (
              <LinkWrapper
                ctaVariant="primary"
                field={requestAnAppointment}
                className="lg:inline-block block md:w-fit w-full"
              />
            )}
            {(phone?.value || isEditing) && (
              <LinkWrapper
                ctaVariant="secondary"
                field={{
                  value: {
                    href: `tel:${phone?.value}`,
                  },
                }}
                className="lg:inline-block block md:w-fit w-full"
              >
                <PlainTextWrapper field={phone} />
              </LinkWrapper>
            )}
          </div>
          <div className="flex items-center lg:mt-16 mt-12">
            <div className="font-nunitoSans text-18 leading-[31px] text-gray-900 font-bold">
              <PlainTextWrapper field={patientReferralLabel} />
            </div>
            <LinkWrapper ctaVariant="hyperlink" field={patientReferralLink} className="ml-4" />
          </div>
        </div>
        {/*Tablet CTA end */}
      </div>
    </ComponentWrapper>
  );
};

export default ProviderHeader;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
  },
  slots: {
    base: [
      'bg-theme-bg',
      'relative',
      'lg:pt-[64px]',
      'lg:pb-[56px]',
      'md:pt-[40px]',
      'md:pb-[40px]',
    ],
    HeaderContentWrapper: ['max-w-screen-xxl', 'm-auto', 'lg:px-20', 'px-10'],
    ProviderContent: ['flex', 'md:flex-row', 'flex-col'],
    ImageContentWrapper: ['lg:mr-[64px]', 'md:mr-[40px]', 'mr-0'],
    ProviderImage: [
      'rounded-md',
      'mb-[26px]',
      'lg:w-[261px] lg:h-[327px]',
      'md:w-[270px] md:h-auto',
      'w-full h-auto',
      'object-cover',
      'object-top',
      'lg:aspect-[3/2]',
      'aspect-[1/1]',
    ],
    ProviderProxyImage: [
      'bg-blue-100',
      'rounded-md',
      'mb-[26px]',
      'lg:w-[261px] lg:h-[327px]',
      'md:w-[270px] md:h-[270px]',
      'w-full h-[335px]',
      'flex items-center justify-center',
    ],
    Title: [
      'font-centuryGothic',
      'lg:text-[48px]',
      'text-[36px]',
      'font-bold',
      'lg:leading-[58px]',
      'leading-[48px]',
      'text-theme-text',
    ],
  },

  variants: {},
});
